import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './AdminPosts.css';
import { Circles } from 'react-loader-spinner';
import privateAxios from '../../utils/axios/privateAxios';
import { FaEdit, FaTrash } from 'react-icons/fa';
import moment from 'moment';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import Notification from '../../components/Notification/Notification';

const AdminPosts = () => {
  const navigate = useNavigate();
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [posts, setPosts] = useState([]);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: '',
    onConfirm: null,
    loading: false,
  });

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => {
      setNotification({ type: '', message: '' });
    }, 5000);
  };

  const fetchPosts = async (page = 1) => {
    try {
      setLoading(true);
      const response = await privateAxios.get(`/private/admin-posts?page=${page}`);
      setPosts(response.data.posts);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching posts', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchedPosts = async (page = 1) => {
    try {
      setLoading(true);
      const response = await privateAxios.get(`/private/admin-search-posts?query=${searchTerm.trim()}&page=${page}`);
      setPosts(response.data.posts);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error searching posts', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setSearchMode(false);
      fetchPosts(1);
    } else {
      setSearchMode(true);
      fetchSearchedPosts(1);
    }
  };

  const handleCheckbox = (id) => {
    setSelectedPosts((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const deleteSelectedPosts = async () => {
    if (!selectedPosts.length) return;
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to delete ${selectedPosts.length} selected post${selectedPosts.length > 1 ? 's' : ''}?`,
      onConfirm: async () => {
        try {
          setConfirmDialog((prev) => ({ ...prev, loading: true }));
          const response = await privateAxios.post('/private/delete-selected-posts', { ids: selectedPosts });
          setSelectedPosts([]);
          setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
          if (response.status === 200) {
            setNotification({type: 'success', message: response.data.message});
            setTimeout(() => {
              setNotification({type: '', message: ''});
            }, 2500);
          }
          searchMode ? fetchSearchedPosts(currentPage) : fetchPosts(currentPage);
        } catch (error) {
          console.error('Error deleting posts', error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false
    });
  };

  const handleDeleteSingle = (id, title) => {
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to delete the post titled "${title}"?`,
      onConfirm: async () => {
        try {
          setConfirmDialog((prev) => ({ ...prev, loading: true }));
          await privateAxios.delete(`/private/delete-post/${id}`);
          setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
          searchMode ? fetchSearchedPosts(currentPage) : fetchPosts(currentPage);
        } catch (error) {
          console.error('Error deleting post', error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false
    });
  };

  const handleEdit = (id) => navigate(`/admin-edit-post/${id}`);

  const handleClickPost = (id, e) => {
    if (e.target.closest('button') || e.target.closest('input')) return;
    navigate(`/admin-post/${id}`);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      searchMode ? fetchSearchedPosts(nextPage) : fetchPosts(nextPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      searchMode ? fetchSearchedPosts(prevPage) : fetchPosts(prevPage);
    }
  };

  return (
    <div className="admin-posts">
      <Notification
              type={notification.type}
              message={notification.message}
              onClose={() => setNotification({ type: '', message: '' })}
            />
      <div className="admin-posts-header">
        <Link to={'/create-post'} className='admin-posts-create-post'>Create Post</Link>
        {posts.length > 0 &&
          <div className='admin-posts-search-input'>
            <input
              type="text"
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className='admin-posts-search-btn' onClick={handleSearch}>Search</button>
          </div>}

        {posts.length > 0 &&
          <button
            className="admin-posts-delete-selected-btn"
            style={{ backgroundColor: selectedPosts.length ? '#008751' : '#ccf1e4' }}
            onClick={deleteSelectedPosts}
            disabled={!selectedPosts.length}
          >
            Delete Selected
          </button>}
      </div>

      <div className="admin-posts-list">
        {loading ? (
          <div style={{ display: 'flex', columnGap: '10px', alignItems: 'center', justifyContent: 'center', height: '300px', color: 'gray' }}>
            <Circles height="50" width="50" color="#008751" ariaLabel="loading" /> <h2>Loading Posts...</h2>
          </div>
        ) : posts.length ? (
          posts.map((p) => (
            <div className="admin-posts-card" key={p._id} onClick={(ev) => handleClickPost(p.slug, ev)}>
              <div className='admin-posts-checkbox-title'>
                <input
                  type="checkbox"
                  checked={selectedPosts.includes(p._id)}
                  onChange={() => handleCheckbox(p._id)}
                />
                <div className="admin-posts-info">
                  <h4>
                    {p.title.split(' ').length > 10
                      ? p.title.split(' ').slice(0, 10).join(' ') + '...'
                      : p.title}
                  </h4>

                  <small>
                    <p>Posted By: <strong>{p.createdBy?.firstname} {p.createdBy?.lastname}</strong></p>
                    <p>On: <strong>{moment(p.createdAt).format('MMMM DD, YYYY')}</strong></p>
                    {p.updatedAt && ` | Last Edited: ${moment(p.updatedAt).format('MMMM DD, YYYY')}`}
                  </small>
                </div>
              </div>

              <div className="admin-posts-actions">
                <button className='admin-posts-edit-button' onClick={() => handleEdit(p.slug)}><FaEdit /></button>
                <button
                  className='admin-posts-delete-button'
                  onClick={() => handleDeleteSingle(p._id, p.title)}
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div>No posts found.</div>
        )}
      </div>

      {posts.length > 10 &&
        <div className="admin-posts-pagination-controls">
          <button onClick={handlePreviousPage} disabled={currentPage === 1}>Previous</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
        </div>}

      <div className="admin-reports-back-to-admin-dashboard">
        <Link to={'/admin-dashboard'}>Back to Management Dashboard</Link>
      </div>

      {confirmDialog.open && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false })}
          loading={confirmDialog.loading}
        />
      )}
    </div>
  );
};

export default AdminPosts;